import * as React from "react"
import { CheckIcon, ChevronDownIcon } from "lucide-react"

import { buttonVariants } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"

export type SearchableSelectOption = {
  value: string
  label: string
}

export type SearchableSelectProps = {
  options: SearchableSelectOption[]
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  searchPlaceholder: string
  ariaLabel: string
  clearLabel: string
  allowClear?: boolean
  disabled?: boolean
  emptyStateLabel?: string
  className?: string
}

type OpenHighlightMode = "selected" | "first" | "last"
type CloseAutoFocusMode = "trigger" | "none"

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  ariaLabel,
  clearLabel,
  allowClear = true,
  disabled = false,
  emptyStateLabel = "Nenhum resultado encontrado",
  className,
}: SearchableSelectProps) {
  const listboxId = React.useId()
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const openHighlightModeRef = React.useRef<OpenHighlightMode>("selected")
  const closeAutoFocusModeRef = React.useRef<CloseAutoFocusMode>("trigger")
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState("")
  const [contentWidth, setContentWidth] = React.useState<number>()
  const [highlightedValue, setHighlightedValue] = React.useState<string>("")

  const normalizedSearchTerm = searchTerm.trim().toLowerCase()
  const filteredOptions = normalizedSearchTerm
    ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearchTerm))
    : options
  const displayedOptions = allowClear ? [{ value: "", label: clearLabel }, ...filteredOptions] : filteredOptions
  const selectedOption = options.find((option) => option.value === value) ?? null
  const highlightedIndex = displayedOptions.findIndex((option) => option.value === highlightedValue)
  const highlightedOptionId = highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined
  const scrollAreaHeight = Math.min(Math.max(displayedOptions.length, 1) * 40, 240)

  const syncTriggerWidth = React.useCallback(() => {
    if (!triggerRef.current) return

    setContentWidth(triggerRef.current.offsetWidth)
  }, [])

  const clearTransientState = React.useCallback(() => {
    setSearchTerm("")
    setHighlightedValue("")
  }, [])

  const openPopover = (mode: OpenHighlightMode) => {
    if (disabled) return

    closeAutoFocusModeRef.current = "trigger"
    openHighlightModeRef.current = mode
    setOpen(true)
  }

  const closePopover = (mode: CloseAutoFocusMode) => {
    closeAutoFocusModeRef.current = mode
    setOpen(false)
  }

  const handleSelect = (nextValue: string) => {
    closeAutoFocusModeRef.current = "trigger"
    onValueChange(nextValue)
    setOpen(false)
    clearTransientState()
  }

  const moveHighlight = (direction: 1 | -1) => {
    if (displayedOptions.length === 0) return

    const currentIndex = displayedOptions.findIndex((option) => option.value === highlightedValue)

    if (currentIndex === -1) {
      const nextIndex = direction > 0 ? 0 : displayedOptions.length - 1
      setHighlightedValue(displayedOptions[nextIndex]!.value)
      return
    }

    const nextIndex = Math.max(0, Math.min(displayedOptions.length - 1, currentIndex + direction))
    setHighlightedValue(displayedOptions[nextIndex]!.value)
  }

  React.useEffect(() => {
    if (!open) return

    syncTriggerWidth()

    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus()

      if (displayedOptions.length === 0) {
        setHighlightedValue("")
        return
      }

      if (openHighlightModeRef.current === "first") {
        setHighlightedValue(displayedOptions[0]!.value)
        return
      }

      if (openHighlightModeRef.current === "last") {
        setHighlightedValue(displayedOptions[displayedOptions.length - 1]!.value)
        return
      }

      if (displayedOptions.some((option) => option.value === value)) {
        setHighlightedValue(value)
        return
      }

      setHighlightedValue(displayedOptions[0]!.value)
    })

    if (typeof ResizeObserver === "undefined" || !triggerRef.current) {
      return () => window.cancelAnimationFrame(frame)
    }

    const resizeObserver = new ResizeObserver(() => syncTriggerWidth())
    resizeObserver.observe(triggerRef.current)

    return () => {
      window.cancelAnimationFrame(frame)
      resizeObserver.disconnect()
    }
  }, [open, syncTriggerWidth])

  React.useEffect(() => {
    if (!open) return

    if (displayedOptions.length === 0) {
      setHighlightedValue("")
      return
    }

    if (!displayedOptions.some((option) => option.value === highlightedValue)) {
      setHighlightedValue(displayedOptions[0]!.value)
    }
  }, [displayedOptions, highlightedValue, open])

  React.useEffect(() => {
    if (!open || !highlightedOptionId) return

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(highlightedOptionId)?.scrollIntoView({ block: "nearest" })
    })

    return () => window.cancelAnimationFrame(frame)
  }, [highlightedOptionId, open])

  const triggerLabel = selectedOption?.label ?? placeholder

  return (
    <div className={cn("w-full", className)}>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) {
            openHighlightModeRef.current = "selected"
            setOpen(true)
            return
          }

          closeAutoFocusModeRef.current = "none"
          setOpen(false)
          clearTransientState()
        }}
      >
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            disabled={disabled}
            onKeyDown={(event) => {
              if (disabled) return

              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                openPopover("selected")
                return
              }

              if (event.key === "ArrowDown") {
                event.preventDefault()
                openPopover("first")
                return
              }

              if (event.key === "ArrowUp") {
                event.preventDefault()
                openPopover("last")
              }
            }}
            className={cn(
              buttonVariants({ variant: "outline", size: "default" }),
              "h-10 w-full justify-between rounded-lg border-border bg-background px-3 py-2 text-sm font-normal text-foreground shadow-none hover:bg-background hover:text-foreground",
              "focus-visible:border-border focus-visible:ring-ring/70",
              "disabled:bg-muted disabled:text-muted-foreground",
              open && "border-border ring-2 ring-ring/60"
            )}
          >
            <span className={cn("truncate", !selectedOption && "text-muted-foreground")}>{triggerLabel}</span>
            <ChevronDownIcon className={cn("size-4 text-muted-foreground transition-transform", open && "rotate-180")} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          style={contentWidth ? { width: `${contentWidth}px` } : undefined}
          className="rounded-lg border-border p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => {
            if (closeAutoFocusModeRef.current === "none") {
              event.preventDefault()
            }
          }}
        >
          <div className="border-b border-border p-2">
            <Input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              aria-activedescendant={highlightedOptionId}
              onChange={(event) => setSearchTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault()
                  moveHighlight(1)
                  return
                }

                if (event.key === "ArrowUp") {
                  event.preventDefault()
                  moveHighlight(-1)
                  return
                }

                if (event.key === "Enter") {
                  event.preventDefault()

                  const selectedValue = highlightedIndex >= 0
                    ? displayedOptions[highlightedIndex]!.value
                    : displayedOptions[0]?.value

                  if (selectedValue !== undefined) {
                    handleSelect(selectedValue)
                  }
                  return
                }

                if (event.key === "Escape") {
                  event.preventDefault()
                  closePopover("trigger")
                  triggerRef.current?.focus()
                  clearTransientState()
                  return
                }

                if (event.key === "Tab") {
                  closePopover("none")
                  clearTransientState()
                }
              }}
              className="h-9 border-border text-sm shadow-none"
            />
          </div>

          <ScrollArea className="overflow-hidden" style={{ height: `${scrollAreaHeight}px` }}>
            <div role="listbox" id={listboxId} className="p-1">
              {displayedOptions.map((option, index) => {
                const isHighlighted = option.value === highlightedValue
                const isSelected = option.value === value
                const isClearOption = option.value === ""

                return (
                  <button
                    key={option.value || "searchable-select-clear"}
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={isSelected}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlightedValue(option.value)}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm text-foreground",
                      "transition-colors hover:bg-accent focus:outline-none",
                      isClearOption && "text-muted-foreground",
                      isHighlighted && "bg-accent",
                      isSelected && "bg-accent text-foreground"
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <CheckIcon className="ml-3 size-4 shrink-0 text-muted-foreground" />}
                  </button>
                )
              })}
            </div>
          </ScrollArea>

          {filteredOptions.length === 0 && (
            <div className="border-t border-border px-3 py-2 text-sm text-muted-foreground">
              {emptyStateLabel}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
